import styled from 'styled-components';

import Box from './Box';
import { TTypography } from './types';

import CustomIcon from '../Icons/CustomIcon';

import { handleTypography } from '../../../styles/Shared/TypographyStyles';

type TInput = {
    iconPadding?: boolean;
    isInvalid?: boolean;
};

const InputWrapper = styled(Box) <TInput>`
    position: relative;
    display: inline-block;
    width: 100%;

    svg > path {
        fill: ${({ theme, isInvalid }) => !isInvalid ? null : theme.color['error-4']};
    }
`;

const StyledInput = styled.input<TTypography & TInput>`
    // Typography
    ${(props: TTypography) => handleTypography(props)}

    background: ${({ theme, isInvalid }) => !isInvalid ? theme.color['neutral1'] : theme.color['error-1']};
    border-radius: 8px;
    height: 100%;
    width: 100%;
    padding: ${({ iconPadding }) => (iconPadding ? '20px 42px 20px 16px' : '20px 16px')};
    border: none;
    outline: none;

    &:not(:placeholder-shown) {
        padding: ${({ iconPadding }) => (iconPadding ? '28px 42px 12px 16px' : '28px 16px 12px 16px')};
        ~ label {
            position: absolute;
            left: 8px;
            transform: translateY(-12px);
            font-size: 12px;
            font-weight: 400;
            color: ${({ theme }) => theme.color['coolgray-10']};
        }
    }

    &:placeholder-shown {
        ~ label {
            visibility: hidden;
            padding: 1px 16px;
        }

        &:focus-visible {
            box-shadow: none;
            outline: none;
        }
    }

    &::placeholder {
        color: ${({ theme }) => theme.color['coolgray-11']};
    }

    &:hover,
    &:focus {
        background: ${({ theme, isInvalid }) => !isInvalid ? theme.color['neutral2'] : theme.color['error-2']};
    }

    &:focus-visible {
        box-shadow: ${({ theme, isInvalid }) => !isInvalid ? `0 0 0 3px ${theme.color['blue-2']}` : 'none'};
        outline: none;
    }
`;

const Label = styled.label<TTypography>`
    // Typography
    ${(props: TTypography) => handleTypography(props)}

    position: absolute;
    padding: 0 8px;
    bottom: 20px;
    left: 0;
    color: gray;
    pointer-events: none;
    transition: all 0.2s ease-in;
`;

const SideWrapper = styled.div`
    position: absolute;
    top: 20px;
    right: 12px;
`;

type TProps = {
    label?: string;
    sideComponent?: React.ReactElement;
    register?: any;
    errors?: any;
    id: string | number;
    infoMessage?: string,
};

const Input = ({ type, label, placeholder, sideComponent, onChange, onBlur, register, errors, ...props }: React.InputHTMLAttributes<HTMLInputElement> & TProps) => {
    let hasSideComponent;
    let isInvalid = errors ? !!errors[props.id] : false;

    if (sideComponent) hasSideComponent = true;

    return (
        <>
            <InputWrapper isInvalid={isInvalid} mb={isInvalid ? 'sm' : undefined}>
                <StyledInput
                    type={type ? type : 'text'}
                    placeholder={placeholder}
                    onChange={onChange}
                    onBlur={onBlur}
                    required={props.required}
                    typography="text-m-light"
                    iconPadding={hasSideComponent}
                    isInvalid={isInvalid}
                    {...register}
                />
                <Label typography="ui-m">{label}</Label>

                {!hasSideComponent && isInvalid ? <SideWrapper><CustomIcon icon="Information_sm" customColor='error-4' /></SideWrapper> : null}
                {sideComponent ? <SideWrapper>{sideComponent}</SideWrapper> : null}
            </InputWrapper>

            {/* Info Message */}
            {!isInvalid && props.infoMessage ? (
                <Box display="flex" textAlign="left" as="span" typography="ui-s" customColor="coolgray-10" mt="sm">
                    {props.infoMessage}
                </Box>
            ) : null}

            {/* Error Message */}
            {errors && errors[props.id]?.message ? (
                <Box display="flex" textAlign="left" as="span" typography="ui-s" customColor="error-4">
                    {errors[props.id].message}
                </Box>
            ) : null}
        </>
    );
};

export default Input;
